import { c as _c } from "react/compiler-runtime";
/* eslint-disable @typescript-eslint/no-empty-function */
import { useState, useEffect, createContext } from 'react';
import { useContextSelector } from 'use-context-selector';
import { amplitudeInstance } from '../config/amplitude';
import buildRouteUrl from '../helpers/buildRouteUrl';
import { setLanguage } from '../helpers/language';
import { getUserToken, logout } from '../helpers/session';
import api from '../services/api';
import { logoutSendrealmWebPush } from '../services/SendrealmPush';
import { ProfileImage, ProfileVerification, UserSubscription } from '../types/types';
import AppContext from './AppContext';
interface UserData {
  _id?: string;
  name?: string;
  username?: string;
  profile_image: ProfileImage;
  email?: string;
  content_region?: string;
  selected_language?: string;
  gender?: string;
  birthdate?: string;
  email_verified_at?: string;
  verifications?: ProfileVerification[];
}
interface AuthContextI {
  isLoading: boolean;
  isLoadingSubscription: boolean;
  isLoggedIn: boolean;
  token: string | null;
  userData: UserData | null;
  userSubscription: UserSubscription | null;
  setData(data: UserData): void;
  setUserSubscription(data: UserSubscription): void;
  destroySession(): void;
  deleteSession(): void;
}
interface PropTypes {
  value?: AuthContextI;
  children: React.ReactNode;
}
const AuthContext = createContext<AuthContextI>({} as any);
export default AuthContext;
export const AuthProvider: React.FC<PropTypes> = t0 => {
  const $ = _c(21);
  const {
    children
  } = t0;
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [token, setToken] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userSubscription, setUserSubscription] = useState(null);
  const refetchClientConfig = useContextSelector(AppContext, _temp);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async function getUserData() {
      ;
      try {
        const response = await api.get("/v1/me");
        setUserData({
          _id: response.data.data._id,
          name: response.data.data.name,
          username: response.data.data.username,
          email: response.data.data.email,
          profile_image: response.data.data.profile_image,
          gender: response.data.data.gender,
          birthdate: response.data.data.birthdate,
          content_region: response.data.data.content_region,
          selected_language: response.data.data.selected_language,
          email_verified_at: response.data.data.email_verified_at
        });
      } catch (t2) {
        const err = t2;
        if (err?.response?.status === 401) {
          await logoutSendrealmWebPush();
          logout();
          window.location = "/" as unknown as Location;
        }
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const getUserData = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = async function getUserSubscription() {
      ;
      try {
        setIsLoadingSubscription(true);
        const response_0 = await api.get("/v1/me/subscription");
        setUserSubscription(response_0.data.data);
        setIsLoadingSubscription(false);
      } catch (t3) {
        const err_0 = t3;
        setIsLoadingSubscription(false);
        if (err_0?.response?.status === 404) {
          setUserSubscription(null);
        }
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const getUserSubscription = t2;
  let t3;
  if ($[2] !== refetchClientConfig) {
    t3 = () => {
      const getToken = async function getToken() {
        setIsLoading(true);
        const userSession = await getUserToken();
        if (userSession.isLoggedIn) {
          setToken(userSession.token);
          await getUserData();
          refetchClientConfig();
          await getUserSubscription();
        }
        if (!userSession.isLoggedIn) {
          refetchClientConfig();
          setToken(null);
          setUserData(null);
        }
        setIsLoading(false);
      };
      getToken();
    };
    $[2] = refetchClientConfig;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[5] !== userData) {
    t5 = () => {
      if (userData?.selected_language) {
        setLanguage(userData.selected_language);
      }
    };
    t6 = [userData];
    $[5] = userData;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = function setData(data) {
      setUserData(data);
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const setData = t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = async function destroySession() {
      await logoutSendrealmWebPush();
      await api.delete("/v1/logout");
      amplitudeInstance.logEvent("Logout");
      amplitudeInstance.setUserId(null);
      amplitudeInstance.regenerateDeviceId();
      logout();
      window.location = buildRouteUrl("/") as unknown as Location;
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const destroySession = t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = async function deleteSession() {
      await logoutSendrealmWebPush();
      amplitudeInstance.setUserId(null);
      amplitudeInstance.regenerateDeviceId();
      logout();
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const deleteSession = t9;
  const t10 = !!token;
  let t11;
  if ($[11] !== isLoading || $[12] !== isLoadingSubscription || $[13] !== t10 || $[14] !== token || $[15] !== userData || $[16] !== userSubscription) {
    t11 = {
      isLoading,
      isLoadingSubscription,
      isLoggedIn: t10,
      token,
      userData,
      setData,
      setUserSubscription,
      userSubscription,
      destroySession,
      deleteSession
    };
    $[11] = isLoading;
    $[12] = isLoadingSubscription;
    $[13] = t10;
    $[14] = token;
    $[15] = userData;
    $[16] = userSubscription;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== children || $[19] !== t11) {
    t12 = <AuthContext.Provider value={t11}>{children}</AuthContext.Provider>;
    $[18] = children;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  return t12;
};
function _temp(app) {
  return app.refetchClientConfig;
}